import { requireSupabase } from './supabase';

export type RegisteredUser = {
  id: string;
  fullName: string | null;
  phone: string | null;
  refCode: string | null;
  role: 'admin' | 'customer' | null;
  createdAt: string | null;
};

const fetchUsersViaServerApi = async (): Promise<RegisteredUser[]> => {
  const response = await fetch('/api/users');
  if (!response.ok) {
    const message = await response.text();
    throw new Error(`Users API failed (${response.status}): ${message}`);
  }
  return (await response.json()) as RegisteredUser[];
};

export const listRegisteredUsers = async (): Promise<RegisteredUser[]> => {
  const supabase = requireSupabase();

  const { data: profiles, error: profilesError } = await supabase
    .from('profiles')
    .select('id, full_name, phone, ref_code, created_at')
    .order('created_at', { ascending: false });

  if (profilesError) {
    console.warn('Users list warning:', profilesError.message);
    return fetchUsersViaServerApi();
  }

  const userIds = (profiles ?? []).map((item) => String(item.id));
  if (userIds.length === 0) return [];

  // Fetch roles independently — don't let a roles error block the user list.
  const { data: roles, error: rolesError } = await supabase
    .from('user_roles')
    .select('user_id, role')
    .in('user_id', userIds);

  if (rolesError) {
    console.warn('User roles fetch warning:', rolesError.message);
  }

  const roleMap = new Map<string, 'admin' | 'customer'>();
  (roles ?? []).forEach((item) => {
    const role = item.role;
    if (role === 'admin' || role === 'customer') {
      roleMap.set(String(item.user_id), role);
    }
  });

  const users = (profiles ?? []).map((item) => ({
    id: String(item.id),
    fullName: item.full_name ?? null,
    phone: item.phone ?? null,
    refCode: item.ref_code ?? null,
    role: roleMap.get(String(item.id)) ?? null,
    createdAt: item.created_at ?? null
  }));

  // If client-side RLS returns only the signed-in user, pull the full list from server API.
  if (users.length <= 1) {
    try {
      const serverUsers = await fetchUsersViaServerApi();
      if (serverUsers.length >= users.length) {
        return serverUsers;
      }
    } catch (error) {
      console.warn('Users API fallback warning:', error);
    }
  }

  return users;
};
